import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BarChart3,
    User,
    Users,
    Building2,
    Map,
    Calendar,
    Filter,
    Download
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { useTickets } from '../../../contexts/TicketContext';

// Import Sub-components (Placeholder for now)
import OverviewTab from './OverviewTab';
import IndividualTab from './IndividualTab';
import SectorTab from './SectorTab';
import CityTab from './CityTab';

const StaffReports = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [dateFilter, setDateFilter] = useState('24h');
    const { user } = useAuth();

    // Permission Check
    const allowedRoles = ['admin', 'quality_analyst', 'head_staff', 'head_moderation', 'head_support', 'direcao'];
    if (!allowedRoles.includes(user?.accessLevel)) {
        return (
            <div className="flex flex-col items-center justify-center h-[80vh] text-center p-8">
                <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
                    <User className="h-10 w-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Acesso Restrito</h2>
                <p className="text-gray-400 max-w-md">
                    Você não tem permissão para visualizar os relatórios de performance.
                </p>
            </div>
        );
    }

    const tabs = [
        { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
        { id: 'individual', label: 'Individual', icon: User },
        { id: 'sector', label: 'Por Setor', icon: Users },
        { id: 'city', label: 'Por Cidade', icon: Map },
    ];

    return (
        <div className="p-6 space-y-6 min-h-screen bg-black/40 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-3">
                        <BarChart3 className="h-8 w-8 text-indigo-400" />
                        Relatórios de Performance
                    </h1>
                    <p className="text-gray-400 mt-2">
                        Análise detalhada de produtividade e qualidade do atendimento.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="bg-black/40 text-white border border-white/10 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none cursor-pointer hover:bg-white/5 transition-colors"
                    >
                        <option value="24h">Últimas 24h</option>
                        <option value="7d">Últimos 7 dias</option>
                        <option value="30d">Últimos 30 dias</option>
                        <option value="month">Este Mês</option>
                        <option value="all">Todo o Período</option>
                    </select>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 relative overflow-hidden group ${isActive
                                ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 shadow-[0_0_15px_rgba(79,70,229,0.1)]'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-transparent'
                                }`}
                        >
                            <Icon className={`h-4 w-4 ${isActive ? 'text-indigo-400' : 'text-gray-500 group-hover:text-gray-300'}`} />
                            <span className="font-medium">{tab.label}</span>
                            {isActive && (
                                <motion.div
                                    layoutId="activeTabIndicator"
                                    className="absolute inset-0 bg-indigo-500/5 rounded-xl"
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content Area */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                >
                    {activeTab === 'overview' && <OverviewTab dateFilter={dateFilter} />}
                    {activeTab === 'individual' && <IndividualTab dateFilter={dateFilter} />}
                    {activeTab === 'sector' && <SectorTab dateFilter={dateFilter} />}
                    {activeTab === 'city' && <CityTab dateFilter={dateFilter} />}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default StaffReports;
